import { useField } from 'formik';
import styled from 'styled-components';
import type { ReactNode, InputHTMLAttributes } from 'react';

import { getColor, getSpace } from '@yoweb/ui/styles/utils/theme';
import { pxToRem } from '@yoweb/ui/styles/utils';

type RadioProps = {
  name: string;
  id: string;
  value?: string | number;
  children?: ReactNode;
  disabled?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  checked?: boolean;
};

type RadioUIProps = InputHTMLAttributes<HTMLInputElement> & RadioProps;

/**
 * A Formik controlled Radio component.
 */
function Radio(props: RadioProps) {
  const [field] = useField({ type: 'radio', ...props });
  return <RadioUI {...field} {...props} />;
}

/**
 * A radio component.
 */
export const RadioUI = ({ children, ...props }: RadioUIProps) => (
  <Wrap htmlFor={props.id}>
    <Control type="radio" {...props} />
    <UI />
    <div>{children}</div>
  </Wrap>
);

/**
 * Related radio controls belong to the same RadioGroup.
 */
export const RadioGroup = styled.div.attrs({ role: 'radiogroup' })`
  display: grid;
  grid-gap: ${getSpace('small3')};
  grid-template-columns: max-content;
`;

const Wrap = styled.label`
  cursor: pointer;
  display: flex;
  align-items: baseline;
  position: relative;
`;

const size = 24;

const UI = styled.div`
  background-color: ${getColor('base000')};
  border: 1px solid;
  border-color: ${getColor('base400')};
  border-radius: 50%;
  flex-shrink: 0;
  margin-right: ${getSpace('small3')};
  pointer-events: none;
  position: relative;
  transform: translateY(25%);
  height: ${pxToRem(size)};
  width: ${pxToRem(size)};
`;

const Control = styled.input`
  height: 0;
  width: 0;
  opacity: 0;
  position: absolute;

  &:active + ${/* sc-selector */ UI}, &:focus + ${UI} {
    border: 3px solid;
    border-color: ${getColor('base900')};
  }

  &:checked + ${UI} {
    border: 1px solid;
    border-color: ${getColor('base900')};

    &::after {
      content: '';
      position: absolute;
      border-radius: 50%;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: ${pxToRem(16)};
      height: ${pxToRem(16)};
      background: ${getColor('base900')};
    }
  }

  &:disabled + ${UI} {
    border-color: ${getColor('base200')};
    background: ${getColor('base050')};

    &::after {
      background: ${getColor('base200')};
    }
  }
`;

export default Radio;
